import React, { useState, useCallback, useEffect, useRef } from "react";
import * as R from "ramda";
import { useIntl } from "react-intl";
import { VStack } from "@chakra-ui/react";
import { useHistory, Route, Switch, useRouteMatch } from "react-router-dom";
import {
  FLOW_STATE,
  isNilOrEmpty,
  noop,
  useCardProvider,
  useSessionCtx,
  hasNoAllowedPaymentOptions,
  useAnalytics,
} from "utils";
import { FullPageSpinner } from "components/Spinner";
import { BlankSlate } from "./BlankSlate";
import { getFinalPaymentMethods } from "./PMFilter";
import { FraudnetPMList } from "components/Paypal/Fraudnet";
import { FraudCheck } from "components/FraudCheck";
import EmptyState from "components/EmptyState";
import Header from "flows/components/Header";
import { useGetTransactionMethods } from "api/transaction-methods";

const pre = "[Chooser]";

export const ChooserConfig = {
  WALLET: "wallet",
  PAYPAL: "paypal",
  BANKING: "banking",
  AddCCButton: "addCC",
  AddPaypalButton: "addPaypal",
  AllowExcludePaypal: "allowExcludePaypal",
};

const providerSortingMap = {
  wallet: 0,
};
const getProviderMapValue = (pm) => R.defaultTo(999, providerSortingMap[pm.provider]);
const byProvider = (a, b) => getProviderMapValue(a) - getProviderMapValue(b);
const byLastUsed = R.descend(R.prop("updated"));
export const sortPaymentMethods = R.sortWith([byProvider, byLastUsed]);

export const Chooser = ({
  onReady,
  onComplete = noop,
  onError,
  chooserConfig = {},
  showPaymentMethodMenu = false,
}) => {
  // analytics
  const analytics = useAnalytics();
  const viewEventFired = useRef(false);

  const { formatMessage } = useIntl();
  const history = useHistory();
  const { path } = useRouteMatch();
  const { debug, sessionCtx } = useSessionCtx();
  const { flowConfig, flow } = sessionCtx;
  debug(pre, "chooserConfig", chooserConfig);

  const { Provider: AddCard } = useCardProvider();
  const transactionMethods = useGetTransactionMethods();

  const refetch = transactionMethods.refetch;

  const [showFullPageSpinner, setShowFullPageSpinner] = useState(false);

  useEffect(() => {
    if (!viewEventFired.current) {
      const fName = (flow || "").toUpperCase();
      analytics.page(`ChooserFlow-${fName}`, `${fName}_Viewed`);
      viewEventFired.current = true;
    }
  }, [analytics, viewEventFired, flow]);

  const handleSelect = (data) => {
    setShowFullPageSpinner(true);

    if (data.state === FLOW_STATE.CANCEL) {
      onComplete(data);
      return;
    }

    onComplete({
      state: FLOW_STATE.COMPLETE,
      id: data.id,
      psp_reference: data.psp_reference,
      pm_state: data.pm_state,
    });
  };

  const handleClose = () => onComplete({ state: "cancel" });

  const handleGoBack = async () => {
    history.push(path);
    await refetch();
  };

  const handlePaymentMethodUpdated = useCallback(async () => {
    refetch();
  }, [refetch]);

  if (showFullPageSpinner) {
    return (
      <div data-testid={`chooser-complete`}>
        <FullPageSpinner data-testid="chooser-complete-spinner" />
      </div>
    );
  }
  if (transactionMethods.isError) {
    return <div data-testid={`chooser-error`}>ERROR</div>;
  }
  if (transactionMethods.isPending) {
    return <FullPageSpinner data-testid="payment-methods-and-card-provider-spinner" />;
  }

  // check here for 0 payment options edge case
  const txnDetails = flowConfig?.transactionDetails;
  const noWayToPay = hasNoAllowedPaymentOptions(transactionMethods.data.payload, txnDetails);
  if (noWayToPay) {
    const err = new Error("No payment options available");
    if (onError) {
      onError(err);
    }
    return <EmptyState error={err} onReady={onReady} />;
  }

  const pmsDict = transactionMethods.data.payload.payment_methods;
  const paymentMethods = Object.keys(pmsDict).map((k) => pmsDict[k]);
  const finalPaymentMethods = getFinalPaymentMethods(paymentMethods, chooserConfig, flowConfig);
  const sortedFinalPaymentMethods = sortPaymentMethods(finalPaymentMethods);
  const hasExisting = !isNilOrEmpty(sortedFinalPaymentMethods);
  const allowedProviders = transactionMethods.data.payload.providers || {};

  debug(pre, "sortedFinalPaymentMethods: ", sortedFinalPaymentMethods);

  const renderheader = () => {
    if (flow === "payout") {
      return (
        <Header
          showLanguage
          title={formatMessage({
            defaultMessage: "Payout",
            description: "Page title",
          })}
          onCloseButtonClick={!flowConfig?.hideExitButtons ? handleClose : undefined}
        />
      );
    } else {
      return null;
    }
  };

  const renderAddCardView = () => {
    switch (flow) {
      case "payout": {
        return (
          <VStack spacing={3} align="stretch">
            <Header
              showLanguage
              title={formatMessage({
                defaultMessage: "Add Credit Card",
                description: "Heading for Add Card flow",
              })}
              onCloseButtonClick={handleGoBack}
            />
            <AddCard onReady={onReady} onComplete={handleGoBack} />
          </VStack>
        );
      }
      case "purchase": {
        return <AddCard onReady={onReady} onComplete={handleGoBack} onCancel={handleGoBack} />;
      }
      default:
        return null;
    }
  };

  return (
    <FraudCheck>
      <div data-testid={`pm-fullpage-count-${transactionMethods.data.payload.length}`} />
      <Switch>
        <Route exact path={`${path}/`}>
          {renderheader()}
          {hasExisting ? (
            <FraudnetPMList
              paymentMethods={sortedFinalPaymentMethods}
              onReady={onReady}
              onSelect={handleSelect}
              chooserConfig={chooserConfig}
              allowedProviders={allowedProviders}
              showMenu={showPaymentMethodMenu}
              onComplete={handlePaymentMethodUpdated}
            />
          ) : (
            <BlankSlate
              onReady={onReady}
              chooserConfig={chooserConfig}
              onCancel={flow === "purchase" ? handleClose : null}
            />
          )}
        </Route>
        <Route path={`${path}/addcc`}>{renderAddCardView()}</Route>
      </Switch>
    </FraudCheck>
  );
};
